import React, { useState, useEffect } from 'react';
import {
  ExternalLink, RefreshCw, Bug, Wrench, Lightbulb,
  CheckCircle2, Clock, AlertCircle, Loader2, Brain, ListChecks, User, Calendar,
  ShieldCheck, KeyRound, LockKeyhole, FolderKanban, CheckSquare, Users,
} from 'lucide-react';
import { echoBrainService } from '../../services/echoBrainService';

interface ChecklistSummary {
  name: string;
  total: number;
  done: number;
}

interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
  type?: string | null;
  created_at?: string;
  created_by?: string;
  source?: string;
}

const TYPE_ICON: Record<string, React.ElementType> = {
  bug: Bug,
  change_request: Wrench,
  feature_request: Lightbulb,
};

const TYPE_COLOR: Record<string, string> = {
  bug: 'text-danger bg-danger-soft border-danger',
  change_request: 'text-warning bg-warning-soft border-warning',
  feature_request: 'text-dev bg-dev-soft border-dev',
};

const COMPLETED_TASKS_KEY = 'echo-brain-local-completed-tasks';
const COMPLETED_ITEMS_KEY = 'echo-brain-local-completed-items';

function loadCompletedSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveCompletedSet(key: string, ids: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {}
}

export default function EchoBrainTab() {
  const [checklists, setChecklists] = useState<ChecklistSummary[]>([]);
  const [selectedChecklist, setSelectedChecklist] = useState<string | null>(null);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [project, setProject] = useState<any>(null);
  const [syncedTasks, setSyncedTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(() => loadCompletedSet(COMPLETED_TASKS_KEY));
  const [completedItemIds, setCompletedItemIds] = useState<Set<string>>(() => loadCompletedSet(COMPLETED_ITEMS_KEY));

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clData, projData, tasksData] = await Promise.all([
        echoBrainService.getFeedbackChecklists(),
        echoBrainService.getCRMProject(),
        echoBrainService.getSyncedTasks('technical').catch(() => ({ tasks: [] })),
      ]);
      setChecklists(clData.checklists || []);
      setProject(projData.data || null);
      setSyncedTasks((tasksData.tasks || []).map((task: any) => (
        completedTaskIds.has(String(task.id)) ? { ...task, status: 'done' } : task
      )));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (name: string) => {
    setSelectedChecklist(name);
    setItemsLoading(true);
    try {
      const data = await echoBrainService.getChecklistItems(name);
      setItems((data.items || []).map((item: ChecklistItem) => ({
        ...item,
        done: item.done || completedItemIds.has(String(item.id)),
      })));
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleTaskDone = (taskId: string) => {
    const id = String(taskId);
    const next = new Set(completedTaskIds);
    const willComplete = !next.has(id);

    if (willComplete) next.add(id);
    else next.delete(id);

    setCompletedTaskIds(next);
    saveCompletedSet(COMPLETED_TASKS_KEY, next);
    setSyncedTasks((current) => current.map((task) => (
      String(task.id) === id
        ? { ...task, status: willComplete ? 'done' : (task.status === 'done' ? 'todo' : task.status) }
        : task
    )));
  };

  const toggleItemDone = (itemId: string) => {
    const id = String(itemId);
    const next = new Set(completedItemIds);
    const willComplete = !next.has(id);

    if (willComplete) next.add(id);
    else next.delete(id);

    setCompletedItemIds(next);
    saveCompletedSet(COMPLETED_ITEMS_KEY, next);
    setItems((current) => current.map((item) => (
      String(item.id) === id ? { ...item, done: willComplete } : item
    )));
  };

  const feedbackLists = Object.values(echoBrainService.CHECKLIST_NAMES);
  const otherLists = checklists.filter(c => !feedbackLists.includes(c.name as any));
  const feedbackSummary = feedbackLists.map(name => {
    const found = checklists.find(c => c.name === name);
    return { name, total: found?.total || 0, done: found?.done || 0 };
  });

  return (
    <div className="w-full max-w-none p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-11 w-11 rounded-xl bg-dev flex items-center justify-center shadow-sm">
              <Brain className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-txt-primary">Echo-Brain</h1>
              <p className="text-sm text-txt-secondary">Project intelligence, technical tasks, and secure operational knowledge for Echomotion.</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-txt-secondary hover:text-txt-primary border border-default rounded-lg hover:bg-bg-surface transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
          <a
            href={echoBrainService.ECHO_BRAIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-dev text-white rounded-lg hover:bg-dev transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Öffnen in Echo-Brain
          </a>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <section className="rounded-2xl border border-dev bg-gradient-to-br from-indigo-50 via-white to-sky-50 p-5 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <FolderKanban className="h-5 w-5 text-dev" />
                <h2 className="text-lg font-bold text-txt-primary">How Echo-Brain Works</h2>
              </div>
              <p className="mt-2 text-sm leading-6 text-txt-secondary">
                Echo-Brain is Echomotion's project manager. Projects are segmented into topic checklists, so technical work, feedback, bugs, changes, and feature requests stay organized and easy to follow.
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-dev bg-white/80 p-3">
              <CheckSquare className="mb-2 h-4 w-4 text-dev" />
              <p className="text-xs font-semibold text-txt-primary">Checklist Driven</p>
              <p className="mt-1 text-xs text-txt-secondary">Every project topic has a clear list of actionable items.</p>
            </div>
            <div className="rounded-xl border border-dev bg-white/80 p-3">
              <Users className="mb-2 h-4 w-4 text-dev" />
              <p className="text-xs font-semibold text-txt-primary">Company Access</p>
              <p className="mt-1 text-xs text-txt-secondary">Everyone on business.echomotion.ai should have access.</p>
            </div>
            <div className="rounded-xl border border-dev bg-white/80 p-3">
              <Brain className="mb-2 h-4 w-4 text-dev" />
              <p className="text-xs font-semibold text-txt-primary">Shared Memory</p>
              <p className="mt-1 text-xs text-txt-secondary">Central context reduces repeated handoffs and lost project details.</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-txt-secondary">
            No access? Contact <a className="font-semibold text-dev hover:underline" href="mailto:juan@echomotion.de">juan@echomotion.de</a>.
          </p>
        </section>

        <section className="rounded-2xl border border-success bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-success text-white shadow-sm">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-txt-primary">Secret Vault Manager</h2>
              <p className="mt-2 text-sm leading-6 text-txt-secondary">
                Echo-Brain includes a Secret Vault Manager for API keys, passwords, SSH keys, and other sensitive project credentials. If users need to store a key or ask for an existing key, they can do it there.
              </p>
            </div>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-success bg-white/80 p-3">
              <KeyRound className="mb-2 h-4 w-4 text-success" />
              <p className="text-xs font-semibold text-txt-primary">One Secure Place</p>
              <p className="mt-1 text-xs text-txt-secondary">No more keys hidden in chats, docs, or private notes.</p>
            </div>
            <div className="rounded-xl border border-success bg-white/80 p-3">
              <LockKeyhole className="mb-2 h-4 w-4 text-success" />
              <p className="text-xs font-semibold text-txt-primary">Controlled Access</p>
              <p className="mt-1 text-xs text-txt-secondary">Sensitive access can stay tied to the right project context.</p>
            </div>
            <div className="rounded-xl border border-success bg-white/80 p-3">
              <ListChecks className="mb-2 h-4 w-4 text-success" />
              <p className="text-xs font-semibold text-txt-primary">Faster Delivery</p>
              <p className="mt-1 text-xs text-txt-secondary">Teams can request the correct credentials without blocking work.</p>
            </div>
          </div>
        </section>
      </div>

      {/* Project info */}
      {project && (
        <div className="rounded-2xl border border-default bg-bg-card p-4 flex flex-col gap-4 shadow-sm sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="text-sm font-semibold text-txt-primary">{project.name}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-txt-secondary">
              <span className="capitalize">{project.lifecycle_stage || 'active'}</span>
              <span>·</span>
              <span className="capitalize">{project.status}</span>
              <span>·</span>
              <span className="capitalize">Priority: {project.priority}</span>
            </div>
          </div>
          <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            project.health_status === 'green' ? 'bg-success-soft text-success' :
            project.health_status === 'yellow' ? 'bg-warning-soft text-warning' :
            'bg-danger-soft text-danger'
          }`}>
            {project.health_status === 'green' ? '● Healthy' :
             project.health_status === 'yellow' ? '● At risk' : '● Critical'}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-txt-secondary py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Lädt Echo-Brain Daten...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-danger bg-danger-soft p-4 text-sm text-danger">
          <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
        </div>
      ) : (
        <>
          {/* Feedback Checklists */}
          <div>
            <h2 className="text-sm font-semibold text-txt-primary mb-3">Feedback Checklists</h2>
            <div className="grid gap-3 md:grid-cols-3">
              {feedbackSummary.map(({ name, total, done }) => {
                const Icon = name === 'Bugs-feedback' ? Bug :
                             name === 'Änderungen-feedback' ? Wrench : Lightbulb;
                const color = name === 'Bugs-feedback' ? 'border-danger bg-danger-soft' :
                              name === 'Änderungen-feedback' ? 'border-warning bg-warning-soft' :
                              'border-dev bg-dev-soft';
                const iconColor = name === 'Bugs-feedback' ? 'text-danger' :
                                  name === 'Änderungen-feedback' ? 'text-warning' : 'text-dev';
                const isSelected = selectedChecklist === name;

                return (
                  <button
                    key={name}
                    onClick={() => isSelected ? setSelectedChecklist(null) : loadItems(name)}
                    className={`rounded-xl border p-4 text-left transition-all hover:shadow-sm ${
                      isSelected ? `${color} ring-2 ring-indigo-400` : 'border-default bg-bg-surface hover:border-dev'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${iconColor}`} />
                      <span className="text-xs font-semibold text-txt-primary">{name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-txt-secondary">
                      <span>{total} items</span>
                      {total > 0 && <span>· {done} done</span>}
                    </div>
                    {total > 0 && (
                      <div className="mt-2 h-1 rounded-full bg-bg-surface overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            name === 'Bugs-feedback' ? 'bg-danger' :
                            name === 'Änderungen-feedback' ? 'bg-warning' : 'bg-dev'
                          }`}
                          style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }}
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Items list */}
            {selectedChecklist && (
              <div className="mt-4 rounded-xl border border-default bg-bg-surface overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-default bg-bg-subtle">
                  <span className="text-sm font-semibold text-txt-primary">{selectedChecklist}</span>
                  {itemsLoading && <Loader2 className="h-4 w-4 animate-spin text-txt-secondary" />}
                </div>
                {items.length === 0 && !itemsLoading ? (
                  <div className="px-4 py-6 text-center text-sm text-txt-secondary">Keine Items vorhanden</div>
                ) : (
                  <div className="divide-y divide-default">
                    {items.map((item) => {
                      const Icon = item.type && TYPE_ICON[item.type] ? TYPE_ICON[item.type] : Clock;
                      return (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleItemDone(item.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleItemDone(item.id);
                            }
                          }}
                          className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer ${
                            item.done ? 'bg-success-soft' : 'hover:bg-bg-card'
                          }`}
                        >
                          <div className={`mt-0.5 flex-shrink-0 rounded-lg p-1 ${item.done ? 'text-success bg-success-soft' : 'text-txt-tertiary bg-bg-card border border-default'}`}>
                            {item.done ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${item.done ? 'line-through text-txt-tertiary' : 'text-txt-primary'}`}>
                              {item.text}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {item.source && (
                                <span className="text-[10px] bg-dev-soft text-dev px-1.5 py-0.5 rounded">
                                  via {item.source}
                                </span>
                              )}
                              {item.created_at && (
                                <span className="text-[10px] text-txt-tertiary">
                                  {new Date(item.created_at).toLocaleDateString('de-DE')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Synced Technical Tasks */}
          {syncedTasks.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-txt-primary mb-3 flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-dev" />
                Synced Technical Tasks ({syncedTasks.length})
              </h2>
              <div className="rounded-2xl border border-default bg-bg-card overflow-hidden shadow-sm">
                <div className="divide-y divide-default">
                  {syncedTasks.map((task: any) => (
                    <div
                      key={task.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleTaskDone(task.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleTaskDone(task.id);
                        }
                      }}
                      className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer ${
                        task.status === 'done' ? 'bg-success-soft' : 'hover:bg-bg-surface'
                      }`}
                      title={task.status === 'done' ? 'Completed locally' : 'Mark as completed locally'}
                    >
                      <div
                        className={`mt-0.5 flex-shrink-0 rounded-lg p-1 transition-colors ${
                          task.status === 'done' ? 'text-success bg-success-soft' : 'text-dev bg-dev-soft border border-dev'
                        }`}
                      >
                        {task.status === 'done'
                          ? <CheckCircle2 className="h-4 w-4" />
                          : <Clock className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${task.status === 'done' ? 'text-txt-tertiary line-through' : 'text-txt-primary'}`}>{task.title}</p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {task.assigned_to_name && (
                            <span className="flex items-center gap-1 text-[11px] text-txt-secondary">
                              <User className="h-3 w-3" />{task.assigned_to_name}
                            </span>
                          )}
                          {task.deadline && (
                            <span className="flex items-center gap-1 text-[11px] text-txt-secondary">
                              <Calendar className="h-3 w-3" />
                              {new Date(task.deadline).toLocaleDateString('de-DE')}
                            </span>
                          )}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            task.priority === 'high' ? 'bg-danger-soft text-danger' :
                            task.priority === 'medium' ? 'bg-warning-soft text-warning' :
                            'bg-success-soft text-success'
                          }`}>{task.priority}</span>
                          <span className="text-[10px] bg-dev-soft text-dev px-1.5 py-0.5 rounded">
                            via {task.source || 'AIDELINE'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
